import React, { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { default as ExpandMoreIcon } from '@mui/icons-material/ExpandMore';
import { default as ExpandLessIcon } from '@mui/icons-material/ExpandLess';
import DeleteControls from '../../../../common/DeleteControls';
import LevelButtonBar from './LevelButtonBar';
import {
  DndProps,
  useDnd,
  verticalListSortingStrategy,
} from '../../../hooks/useSortableDnd';
import { generateDataTestId } from 'src/utils';
import { useMetadata } from '../../../../../contexts/MetadataContext';
import { RoomField } from '../../../../../system/field';
import { Room, RoomGroupType } from '../../../../../system/rooms';

interface LevelProps {
  canAddRooms?: boolean;
  confirmingDelete: string | null;
  disabled?: boolean;
  expandedLevel: boolean;
  handleProps?: any;
  inDeleteMode: boolean;
  isDragging?: boolean;
  isHover?: boolean;
  level: string;
  onAddNewRoom?: (type: string, level: string) => void;
  onAddRoom?: () => void;
  onCancelDelete?: () => void;
  onConfirmDelete?: () => void;
  onDeleteLevel?: (level: string) => void;
  onDeleteRooms?: (rooms: Room[]) => void;
  onEditRoom?: (rooms: Room) => void;
  onRequestDelete?: () => void;
  renderRoom: (dndProps: DndProps, room: Room) => React.ReactElement;
  rooms: Room[];
  roomGroups: RoomGroupType[];
  toggleLevelExpanded: (level: string) => void;
}

export const normalizeLevelLabel = (level: string) => {
  const match = /(upper|lower)\s*(level)?\s*(\d+)/gi.exec(level);
  if (match) {
    return [match[1], 'Level', match[3]].join(' ');
  }

  return level.endsWith('Level') ? level : `${level} Level`;
};

const LevelDetails = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: theme.palette.background.pale,
  marginLeft: theme.spacing(0),
  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(4),
  },
}));

const Level: React.FC<LevelProps> = React.memo(
  ({
    canAddRooms = false,
    confirmingDelete = '',
    expandedLevel,
    handleProps,
    inDeleteMode,
    isDragging = false,
    isHover = false,
    level,
    onAddNewRoom,
    onAddRoom = () => {},
    onCancelDelete = () => {},
    onConfirmDelete = () => {},
    onRequestDelete = () => {},
    renderRoom,
    rooms,
    roomGroups,
    toggleLevelExpanded,
    disabled = false,
  }) => {
    const { SortableContext, SortableItem } = useDnd();
    const { field: getFieldMetadata, displayValueForListingField } =
      useMetadata();

    // Translate the level value if it's a lookup value
    const translatedLevel = useMemo(() => {
      const metadata = getFieldMetadata(RoomField.ROOM_LEVEL);
      if (metadata?.Lookup && level) {
        return displayValueForListingField(metadata.Lookup, level);
      }
      return level;
    }, [level, getFieldMetadata, displayValueForListingField]);

    const isMain = translatedLevel.toLowerCase().includes('main');
    const levelLabel = normalizeLevelLabel(translatedLevel);
    return (
      <SortableContext
        items={rooms.map((room: Room) => room.ROOM_ITEM_NUMBER)}
        strategy={verticalListSortingStrategy}
      >
        <Box
          sx={{
            width: 1,
            mb: 1,
            backgroundColor: 'white',
            opacity: isDragging ? 0.5 : 1,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexGrow: 1,
              mb: 1,
            }}
          >
            <Box
              alignItems="center"
              display="flex"
              onClick={() => toggleLevelExpanded(level)}
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleLevelExpanded(level);
                }}
              >
                {expandedLevel ? (
                  <ExpandLessIcon sx={{ color: 'text.secondary' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: 'text.secondary' }} />
                )}
              </IconButton>
              <Typography
                data-testid={generateDataTestId(
                  levelLabel,
                  undefined,
                  'levelHeader',
                )}
                variant="h4"
                component="span"
                sx={{
                  fontSize: '18px',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  lineHeight: '24px',
                  ml: 1,
                  cursor: 'pointer',
                }}
              >
                {levelLabel} ({rooms.length} Rooms)
              </Typography>
            </Box>
            <Box sx={{ ml: 2 }}>
              {inDeleteMode && !isMain && (
                <DeleteControls
                  deleteButtonText="Delete Level"
                  disabled={false}
                  onCancelDelete={onCancelDelete}
                  onConfirmDelete={
                    confirmingDelete?.length ? onConfirmDelete : onRequestDelete
                  }
                  isConfirming={
                    !!confirmingDelete?.length && confirmingDelete === level
                  }
                />
              )}
            </Box>
          </Box>
          <Collapse in={!!expandedLevel}>
            <LevelDetails
              sx={{
                display: expandedLevel ? 'flex' : 'none',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  mb: rooms.length ? 2 : 0,
                }}
              >
                <LevelButtonBar
                  canAddRooms={canAddRooms}
                  handleProps={handleProps}
                  isDragging={isDragging}
                  onAddNewRoom={(roomType: string) => {
                    onAddNewRoom?.(roomType, level);
                  }}
                  onAddRoom={onAddRoom}
                  rooms={rooms}
                  roomGroups={roomGroups}
                  level={levelLabel}
                  disabled={disabled}
                />
              </Box>
              <Grid container columnSpacing={2}>
                {rooms.map((room, index) => (
                  <SortableItem
                    key={room.ROOM_ITEM_NUMBER}
                    id={room.ROOM_ITEM_NUMBER}
                    index={index}
                    renderItem={(dndProps: DndProps) => {
                      return renderRoom(dndProps, room);
                    }}
                    disabled={rooms.length <= 1}
                  />
                ))}
              </Grid>
            </LevelDetails>
          </Collapse>
        </Box>
      </SortableContext>
    );
  },
);

export default Level;
